import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { templateTabbedSectionRelayFragment$key } from "@/__generated__/templateTabbedSectionRelayFragment.graphql.ts";
import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { SanitizedRichText } from "@/components/confluence/util/sanitized-rich-text";

import { TemplateBenefits } from "../components/template-benefits-relay.tsx";
import { TemplateGuide } from "../components/template-guide-relay.tsx";

const tabbedSectionHeading = css({
    "fontSize": "2.25rem",
    "lineHeight": "2.75rem",
    "fontWeight": 500,
    "letterSpacing": 0.3,
    "color": "#1d3059",
    "fontFamily": "var(--bxp-charlie-display-font-family)",
    "margin": 0,
    "marginBlockEnd": 40,

    "@media (max-width: 47.99rem)": {
        fontSize: "1.5rem",
        lineHeight: "1.75rem"
    }
});

const tabStyles = css({
    "backgroundColor": "#F0F1F2",
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "padding": "8px 24px 8px 24px",
    "borderRadius": "24px",
    "cursor": "pointer",
    "border": "2px solid #F0F1F2",
    "fontSize": "18px",
    ":hover": {
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        border: "2px solid #0C66E4",
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#E9F2FE",
        // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0C66E4"
    }
});

const currentStyle = css({
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "2px solid #0C66E4",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#E9F2FE",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#0C66E4"
});

const tabSectionStyle = css({
    marginBlockStart: "56px"
});

const TabHeading = ({
    title,
    isSelected
}: {
    title: string;
    isSelected?: boolean;
}) => {
    return (
        <div css={[tabStyles, isSelected && currentStyle]} className="h5">
            {title}
        </div>
    );
};

interface TemplateTabbedSectionProps {
    template: templateTabbedSectionRelayFragment$key;
}

const TemplateTabbedSectionRelayFragment = graphql`
    fragment templateTabbedSectionRelayFragment on ContentPlatformTemplate {
        templateOverview {
            overviewTitle
            overviewDescription
        }
        templateBenefits {
            ...templateBenefitsRelayFragment
        }
        howToUseThisTemplate {
            ...templateGuideRelayFragment
        }
        templateProductRationale {
            rationaleTitle
            templateProductRationaleDescription
        }
    }
`;

export const TemplateTabbedSection = ({
    template: templateRef
}: TemplateTabbedSectionProps) => {
    const intl = useIntl();

    const template = useFragment(
        TemplateTabbedSectionRelayFragment,
        templateRef
    );

    const tabs = [
        {
            id: "tab1",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.overview.cfind.1508",
                defaultMessage: "Overview"
            }),
            hide: !template.templateOverview,
            component: (
                <section css={tabSectionStyle}>
                    {template.templateOverview?.map((overview, index) => (
                        <section key={`overview-${index}`}>
                            <h2 css={tabbedSectionHeading}>
                                {overview.overviewTitle}
                            </h2>
                            {overview.overviewDescription && (
                                <SanitizedRichText
                                    innerHTML={overview.overviewDescription}
                                />
                            )}
                        </section>
                    ))}
                </section>
            )
        },
        {
            id: "tab2",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.benefits.cfind.1508",
                defaultMessage: "Benefits"
            }),
            hide:
                !template.templateBenefits ||
                template.templateBenefits.length === 0,
            component: (
                <section>
                    {template.templateBenefits?.map(
                        (templateBenefitsContainer, index) => (
                            <TemplateBenefits
                                key={`benefits-${index}`}
                                templateBenefits={templateBenefitsContainer}
                            />
                        )
                    )}
                </section>
            )
        },
        {
            id: "tab3",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.howTo.cfind.1508",
                defaultMessage: "How to"
            }),
            hide:
                !template.howToUseThisTemplate ||
                template.howToUseThisTemplate.length === 0,
            component: (
                <section>
                    {template.howToUseThisTemplate?.map(
                        (templateGuide, index) => (
                            <TemplateGuide
                                key={`how2use-${index}`}
                                templateGuide={templateGuide}
                            />
                        )
                    )}
                </section>
            )
        },
        {
            id: "tab4",
            title: intl.formatMessage({
                id: "ConfluenceTemplateMessages.tabs.whyConfluence.cfind.1508",
                defaultMessage: "Why Confluence?"
            }),
            // temporary content for demoing... will be replaced with content from template object
            hide: !template.templateProductRationale,

            component: (
                <section css={tabSectionStyle}>
                    {template.templateProductRationale?.map(
                        (rationale, index) => (
                            <section key={`rationale-${index}`}>
                                <h2 css={tabbedSectionHeading}>
                                    {rationale.rationaleTitle}
                                </h2>
                                {rationale.templateProductRationaleDescription && (
                                    <SanitizedRichText
                                        innerHTML={
                                            rationale.templateProductRationaleDescription
                                        }
                                    />
                                )}
                            </section>
                        )
                    )}
                </section>
            )
        }
    ];

    const styles = css({
        "display": "flex",
        "@media(min-width: 768px)": {
            justifyContent: "center"
        }
    });

    return (
        <section
            data-testid="template-tabbed-section"
            css={css({
                "color": " #172B4D",
                "paddingBlockStart": "64px",
                "width": "100%",
                "display": "flex",
                "flexDirection": "column",
                "@media(max-width: 768px)": {
                    overflow: "scroll",
                    width: "100%"
                }
            })}
        >
            <Adg4Tabs>
                <Adg4DraggableTabList css={styles}>
                    {tabs
                        .filter((tab) => !tab.hide)
                        .map((tab) => (
                            <Adg4DraggableTab key={tab.id} title={tab.title}>
                                <TabHeading title={tab.title} />
                            </Adg4DraggableTab>
                        ))}
                </Adg4DraggableTabList>
                {tabs
                    .filter((tab) => !tab.hide)
                    .map((tab, index) => (
                        <Adg4TabPanel key={index} keepInDom={true}>
                            <>{tab.component}</>
                        </Adg4TabPanel>
                    ))}
            </Adg4Tabs>
        </section>
    );
};
